import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { FundInput } from '@tahi/core';
import { addFund, deleteFund, editFund, restoreFund } from '../../../data/commands/budget';
import { useCommandEnv } from '../../../data/hooks';
import { Button, Sheet, showToast } from '../../../ui';
import { useGoBack } from '../../spending/hooks';
import { FundForm } from '../components/FundForm';
import { copy, errorText } from '../copy';
import { useAccounts, useFunds, useIncomeSources } from '../hooks';

export function FundFormRoute() {
  const { id } = useParams();
  const env = useCommandEnv();
  const navigate = useNavigate();
  const goBack = useGoBack(id ? `/m/budget/funds/${id}` : '/m/budget/funds');
  const funds = useFunds();
  const sources = useIncomeSources();
  const accounts = useAccounts() ?? [];
  const [error, setError] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);

  if (funds === undefined || sources === undefined) return null;
  const fund = id ? funds.find((f) => f.id === id) : undefined;
  if (id && !fund) {
    return (
      <Sheet open onOpenChange={(open) => !open && goBack()} title={copy.editFund}>
        <p className="text-muted">{errorText('not_found')}</p>
      </Sheet>
    );
  }
  const salary = sources
    .filter((s) => s.kind === 'salary')
    .map((s) => ({ id: s.id, name: s.name }));

  async function onSubmit(input: FundInput) {
    const r = fund ? await editFund(env, fund.id, input) : await addFund(env, input);
    if (!r.ok) {
      setError(errorText(r.error));
      return;
    }
    goBack();
  }

  async function onDelete() {
    if (!fund) return;
    if (!armed) {
      setArmed(true);
      return;
    }
    const r = await deleteFund(env, fund.id);
    if (r.ok) {
      showToast({
        message: copy.deletedFund(fund.name),
        action: {
          label: copy.undo,
          onPress: () => void restoreFund(env, fund.id, { makeDefault: r.wasDefault }),
        },
      });
    }
    navigate('/m/budget/funds', { replace: true });
  }

  return (
    <Sheet
      open
      onOpenChange={(open) => !open && goBack()}
      title={fund ? copy.editFund : copy.addFund}
    >
      <FundForm
        initial={
          fund
            ? {
                name: fund.name,
                amountMinor: fund.amountMinor,
                period: fund.period,
                refillFromIncomeSourceId: fund.refillFromIncomeSourceId,
                accountIds: fund.accountIds,
              }
            : undefined
        }
        salarySources={salary}
        accounts={accounts}
        error={error}
        onSubmit={onSubmit}
      />
      {fund ? (
        <Button variant="destructive" block className="mt-3" onClick={() => void onDelete()}>
          {armed ? copy.confirmDelete : copy.delete}
        </Button>
      ) : null}
    </Sheet>
  );
}
